/* Сжатие PDF: выбрать файл, выбрать степень, посмотреть «было → стало»
   и забрать результат — скачать на устройство или получить от бота.

   Загрузка и сжатие идут долго, поэтому оба этапа показаны полосой: сначала
   отправка файла (её считает браузер), потом страницы, которые прошёл
   Ghostscript на сервере. Прогресс сжатия мини-апп спрашивает раз в полсекунды. */

import { Button, List, ListItem, Segmented, SegmentedButton } from "konsta/react";
import React, { useEffect, useRef, useState } from "react";

import { Ion, IconTile } from "../components/icons.jsx";
import Screen from "../components/Screen.jsx";
import { Bar, Hint } from "../components/ui.jsx";
import { api, uploadWithProgress } from "../lib/api.js";
import { useFetch } from "../lib/store.jsx";
import { alertDialog, canDownload, downloadFile, haptic } from "../lib/tg.js";

const ITEM_COLORS = {
  primaryTextIos: "text-[color:var(--tg-text)]",
  secondaryTextIos: "text-[color:var(--tg-hint)]",
};

const TILE = { file: "#ff9500", save: "#af52de" };

/* Вес файла: мегабайты с десятыми, мелочь — в килобайтах */
const size = (n) =>
  !n ? "0 КБ" : n >= 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} МБ` : `${Math.max(1, Math.round(n / 1024))} КБ`;

/* Telegram открывает ссылку сам, вне страницы — относительный путь ему не годится */
const absolute = (path) => new URL(path, window.location.origin).toString();

export default function Pdf() {
  const { data: status } = useFetch("/pdf/status");
  const input = useRef(null);

  const [file, setFile] = useState(null);
  const [level, setLevel] = useState("medium");
  /* idle → upload → work → done | error */
  const [stage, setStage] = useState("idle");
  const [uploaded, setUploaded] = useState(0);
  const [job, setJob] = useState(null);
  const [error, setError] = useState(null);
  const [sending, setSending] = useState(false);

  const levels = status?.levels || [];
  const busy = stage === "upload" || stage === "work";

  /* Пока сервер жуёт файл, спрашиваем его о ходе дела */
  useEffect(() => {
    if (stage !== "work" || !job?.id) return undefined;
    let alive = true;

    const tick = async () => {
      try {
        const next = await api.get(`/pdf/jobs/${job.id}`);
        if (!alive) return;
        setJob(next);
        if (next.status === "done") {
          setStage("done");
          haptic.success();
        } else if (next.status === "error") {
          setStage("error");
          setError(next.error || "Не получилось сжать файл");
          haptic.error();
        }
      } catch (e) {
        if (!alive) return;
        setStage("error");
        setError(e.message);
      }
    };

    const timer = setInterval(tick, 500);
    tick();
    return () => {
      alive = false;
      clearInterval(timer);
    };
  }, [stage, job?.id]);

  const pick = (e) => {
    const chosen = e.target.files?.[0];
    e.target.value = "";
    if (!chosen) return;
    if (status?.maxBytes && chosen.size > status.maxBytes) {
      haptic.error();
      alertDialog(`Файл больше ${size(status.maxBytes)} — столько не принимаем.`);
      return;
    }
    haptic.light();
    setFile(chosen);
    setJob(null);
    setError(null);
    setStage("idle");
  };

  const start = async () => {
    if (!file || busy) return;
    haptic.medium();
    setStage("upload");
    setUploaded(0);
    setError(null);
    setJob(null);
    try {
      const created = await uploadWithProgress("/pdf/compress", file, {
        query: { level },
        onProgress: setUploaded,
      });
      setJob(created);
      setStage("work");
    } catch (e) {
      setStage("error");
      setError(e.message);
      haptic.error();
    }
  };

  const send = async () => {
    if (sending) return;
    haptic.medium();
    setSending(true);
    try {
      await api.post(`/pdf/jobs/${job.id}/send`, {});
      haptic.success();
      alertDialog("Отправил — файл в переписке с ботом.");
    } catch (e) {
      haptic.error();
      alertDialog(e.message);
    } finally {
      setSending(false);
    }
  };

  const save = async () => {
    haptic.light();
    await downloadFile(absolute(job.download), `${file?.name?.replace(/\.pdf$/i, "") || "файл"} (сжатый).pdf`);
  };

  const again = () => {
    haptic.light();
    setFile(null);
    setJob(null);
    setError(null);
    setStage("idle");
  };

  /* Одна полоса на оба этапа: отправка файла и работа над ним */
  const percent =
    stage === "upload" ? Math.round(uploaded * 100) : stage === "work" ? (job?.percent ?? null) : 100;
  /* Доля не дублируется в подписи — она стоит справа от неё, под полосой */
  const caption =
    stage === "upload"
      ? "Отправляю файл"
      : job?.pages
        ? `Сжимаю · страница ${job.page || 0} из ${job.pages}`
        : "Сжимаю…";

  const saved = job && job.status === "done" && !job.grew ? 1 - job.after / job.before : 0;

  return (
    <Screen back title="Сжатие PDF" bottomPad={40}>
      <input ref={input} type="file" accept="application/pdf,.pdf" className="hidden" onChange={pick} />

      <List strong inset>
        <ListItem
          link
          chevron={!busy}
          colors={ITEM_COLORS}
          media={<IconTile icon={Ion.documentTextSolid} bg={TILE.file} />}
          title={file ? file.name : "Выбрать PDF"}
          after={file ? size(file.size) : null}
          onClick={busy ? undefined : () => input.current?.click()}
        />
      </List>

      {file && stage !== "done" ? (
        <>
          <div className="px-8 pb-1.5 pt-6 text-[13px]" style={{ color: "var(--tg-hint)" }}>
            Степень сжатия
          </div>
          <div className="px-4">
            <Segmented strong rounded>
              {levels.map((l) => (
                <SegmentedButton
                  key={l.id}
                  active={level === l.id}
                  onClick={() => {
                    if (busy) return;
                    haptic.select();
                    setLevel(l.id);
                  }}
                >
                  <span className="whitespace-nowrap">{l.label}</span>
                </SegmentedButton>
              ))}
            </Segmented>
          </div>
          <div className="px-4 pt-2">
            <Hint>{levels.find((l) => l.id === level)?.hint}</Hint>
          </div>
        </>
      ) : null}

      {busy ? (
        <div className="px-4 pt-6">
          <Bar value={percent ?? 100} />
          <div className="pt-2 flex items-center justify-between gap-3">
            <Hint>{caption}</Hint>
            {percent != null ? <span className="num text-[13px] font-semibold">{percent}%</span> : null}
          </div>
        </div>
      ) : null}

      {file && stage !== "done" && !busy ? (
        <div className="px-4 pt-6">
          <Button large rounded onClick={start} disabled={!status?.ready}>
            Сжать
          </Button>
        </div>
      ) : null}

      {status && !status.ready ? (
        <div className="px-4 pt-3">
          <Hint>Сжимать нечем: на сервере не установлен Ghostscript.</Hint>
        </div>
      ) : null}

      {stage === "error" ? (
        <div className="px-4 pt-4">
          <div className="card-tg px-4 py-3.5 flex gap-2.5">
            <Ion.warning size={20} style={{ color: "var(--fin-red)" }} />
            <div className="text-[14px] leading-snug">{error}</div>
          </div>
        </div>
      ) : null}

      {stage === "done" && job ? (
        <>
          <div className="px-4 pt-4">
            <div className="card-tg px-4 py-4">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="eyebrow px-0 pt-0 pb-1">Было</div>
                  <div className="num text-[19px] font-bold">{size(job.before)}</div>
                </div>
                <div className="min-w-0 text-right">
                  <div className="eyebrow px-0 pt-0 pb-1">Стало</div>
                  <div
                    className="num text-[19px] font-bold"
                    style={{ color: job.grew ? "var(--tg-text)" : "var(--fin-green)" }}
                  >
                    {size(job.grew ? job.before : job.after)}
                  </div>
                </div>
              </div>

              {job.grew ? (
                <div className="mt-3 text-[13px] leading-snug" style={{ color: "var(--tg-hint)" }}>
                  Меньше не стало — картинки внутри уже сжаты. Отдаём исходный файл,
                  на более сильной степени качество упадёт, а вес почти нет.
                </div>
              ) : (
                <>
                  <div className="mt-3">
                    <Bar value={Math.round(saved * 100)} color="var(--fin-green)" />
                  </div>
                  <div className="mt-1.5 text-[13px]" style={{ color: "var(--tg-hint)" }}>
                    Легче на {Math.round(saved * 100)}%
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="px-4 pt-4">
            <Button large rounded onClick={send} disabled={sending || !job.sendable}>
              {sending ? "Отправляю…" : "Отправить в Telegram"}
            </Button>
          </div>

          {!job.sendable ? (
            <div className="px-4 pt-3">
              <Hint>Файл больше 50 МБ — столько Telegram не принимает, остаётся скачать.</Hint>
            </div>
          ) : null}

          <List strong inset>
            {canDownload() ? (
              <ListItem
                link
                chevron={false}
                colors={ITEM_COLORS}
                media={<IconTile icon={Ion.downloadSolid} bg={TILE.save} />}
                title="Сохранить в файлы"
                onClick={save}
              />
            ) : null}
            <ListItem
              link
              chevron={false}
              colors={ITEM_COLORS}
              media={<IconTile icon={Ion.documentTextSolid} bg={TILE.file} />}
              title="Сжать другой файл"
              onClick={again}
            />
          </List>
        </>
      ) : null}

      {!file && stage === "idle" ? (
        <div className="px-4 pt-4">
          <Hint>
            Вес презентации держат картинки внутри неё — сжатие пережимает их, оставляя
            текст и шрифты нетронутыми. Сильное годится для отправки и просмотра с экрана,
            лёгкое — если файл потом пойдёт в печать.
          </Hint>
        </div>
      ) : null}
    </Screen>
  );
}
